import {
  TextField,
  NumberField,
  ReferenceField,
  ListContextProvider,
  useListController,
  TopToolbar,
  ExportButton,
  CreateButton,
  SelectColumnsButton,
  FunctionField,
  useRecordContext,
} from "react-admin";
import { ChunkedExportButton } from "@/components/common/ChunkedExportButton";
import { Download as DownloadIcon } from "@mui/icons-material";
import { Typography, Link } from "@mui/material";
import { getProps } from "@/utils/helpers/columnsHelper";
import { StatusChipField } from "@/components/common/fields/StatusChipField";
import List from "@/components/ui/react-admin/styledList/StyledList";
import { StyledDatagridConfigurable } from "@/components/ui/react-admin/StyledDatagrid";
import { PageHeader } from "@/components/common/PageHeader";
import { getResourceIcon } from "@/utils/icons";
import { ClaimItemListAside } from "./components/ClaimItemListAside";
import { ClaimItem, ResourceName, StoplossClaim } from "@/types";
import { jsonExporter } from "@/utils/exporters/jsonExporter";
import { defaultCurrencyFormatterOptions, formatUSD } from "@/utils/formatters/currencyFormatter";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ListEmptyState } from "@/components/common/ListEmptyState";
import { isNonProductionEnvironment } from "@/utils/environment";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { Link as RouterLink } from "react-router-dom";

/** Shows linked stoploss claim as type + amount link, or "Unlinked" */
const LinkedClaimCell = () => {
  const record = useRecordContext<ClaimItem>();
  const isLinked =
    record?.stoploss_claim_id_ref &&
    record.stoploss_claim_id_ref !== "" &&
    record.stoploss_claim_id_ref !== "00000000-0000-0000-0000-000000000000";

  if (!isLinked) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ fontSize: "0.8rem" }}>
        Unlinked
      </Typography>
    );
  }

  return (
    <ReferenceField source="stoploss_claim_id_ref" reference={ResourceName.STOPLOSS_CLAIMS} link={false}>
      <LinkedClaimLabel />
    </ReferenceField>
  );
};

const LinkedClaimLabel = () => {
  const claim = useRecordContext<StoplossClaim>();
  if (!claim)
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );

  const type = formatToTitleCase(claim.stoploss_claim_type || "Claim");
  const amount = claim.total_paid_amount ? formatUSD(claim.total_paid_amount) : "";
  const label = amount ? `${type} · ${amount}` : type;

  return (
    <Link
      component={RouterLink}
      to={`/stoploss-claims/${claim.id}/show`}
      underline="always"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      sx={{ fontSize: "0.8rem", fontWeight: 500 }}
      noWrap
    >
      {label}
    </Link>
  );
};

const ClaimItemsListActions = ({ loading }: { loading?: boolean }) => {
  const { canCreate } = useResourcePermissions(ResourceName.CLAIM_ITEMS);

  return (
    <TopToolbar>
      {canCreate && <CreateButton disabled={loading} />}
      <SelectColumnsButton disabled={loading} />
      <ChunkedExportButton disabled={loading} />
      {isNonProductionEnvironment() && (
        <ExportButton
          label="Export JSON"
          icon={<DownloadIcon />}
          exporter={(records) => jsonExporter(records, ResourceName.CLAIM_ITEMS)}
          disabled={loading}
        />
      )}
    </TopToolbar>
  );
};

export const ClaimItemsList = () => {
  const listContext = useListController({ resource: ResourceName.CLAIM_ITEMS });
  const { isPending: loading } = listContext;
  const ClaimItemIcon = getResourceIcon(ResourceName.CLAIM_ITEMS);

  return (
    <ListContextProvider value={listContext}>
      <PageHeader
        customTitle="Claim Items"
        hideSectionTitle
        rightContent={<ClaimItemsListActions loading={loading} />}
        headerIcon={<ClaimItemIcon sx={{ fontSize: 28, color: "primary.main" }} />}
      />
      <List
        perPage={50}
        actions={false}
        aside={<ClaimItemListAside />}
        loading={loading}
        empty={<ListEmptyState resource={ResourceName.CLAIM_ITEMS} subtitle="No claim items found" />}
      >
        <StyledDatagridConfigurable
          omit={[
            "aegle_id",
            "date_of_service",
            "procedure_code",
            "primary_diagnosis",
            "diagnosis_description",
            "billed_amount",
            "eligible_amount",
            "member_oop",
            "check_number",
            "service_type",
            "drug_name",
            "is_eligible",
          ]}
        >
          {/* Default visible columns */}
          <FunctionField
            source="provider"
            label="Provider"
            render={(record: ClaimItem) => (
              <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 200 }}>
                {record.provider || "—"}
              </Typography>
            )}
          />
          <ReferenceField
            {...getProps("claimant_id_ref", ResourceName.CLAIM_ITEMS)}
            reference={ResourceName.CLAIMANTS}
            label="Claimant"
          >
            <TextField source="claimant_name" />
          </ReferenceField>
          <ReferenceField
            {...getProps("client_id_ref", ResourceName.CLAIM_ITEMS)}
            reference={ResourceName.CLIENTS}
            label="Client"
          >
            <TextField source="client_legal_name" />
          </ReferenceField>
          <FunctionField
            source="claim_type"
            label="Type"
            render={(record: ClaimItem) => (
              <Typography variant="body2">{formatToTitleCase(record.claim_type || "—")}</Typography>
            )}
          />
          <NumberField
            {...getProps("paid_amount", ResourceName.CLAIM_ITEMS)}
            options={defaultCurrencyFormatterOptions}
          />
          <LinkedClaimCell label="Linked Claim" />
          <StatusChipField {...getProps("claim_status", ResourceName.CLAIM_ITEMS)} />

          {/* Hidden-by-default columns */}
          <TextField {...getProps("aegle_id", ResourceName.CLAIM_ITEMS)} />
          <TextField source="date_of_service" label="Service Date" />
          <TextField source="procedure_code" label="Procedure Code" />
          <TextField source="primary_diagnosis" label="Primary Diagnosis" />
          <TextField source="diagnosis_description" label="Diagnosis Description" />
          <NumberField source="billed_amount" label="Billed" options={defaultCurrencyFormatterOptions} />
          <NumberField source="eligible_amount" label="Eligible" options={defaultCurrencyFormatterOptions} />
          <NumberField source="member_oop" label="Member OOP" options={defaultCurrencyFormatterOptions} />
          <TextField source="check_number" label="Check Number" />
          <TextField source="service_type" label="Service Type" />
          <TextField source="drug_name" label="Drug Name" />
          <FunctionField
            source="is_eligible"
            label="Eligible"
            render={(record: ClaimItem) => (
              <Typography variant="body2" color={record.is_eligible ? "success.main" : "error.main"}>
                {record.is_eligible ? "Yes" : "No"}
              </Typography>
            )}
          />
        </StyledDatagridConfigurable>
      </List>
    </ListContextProvider>
  );
};
